<template>
  <CTabs variant="tabs" class="nav-tabs-boxed">
    <CTab :title="descricaoPluralModulo('ADMIN_FAQ')">
      <div class="faq-listagem">
        <Loading :isActive="loading" />

        <FaqFormPage
          :item="item"
          :tipos="tipos"
          :close="closeEdit"
          :descSingular="descricaoSingularModulo('ADMIN_FAQ')"
        />

        <div class="faq-listagem__header">
          <h4 class="faq-listagem__title">
            {{ descricaoPluralModulo("ADMIN_FAQ") }}
          </h4>
          <CButton color="primary" @click="openCreate">
            <fa-icon icon="plus" class="mr-1" />
            Novo Artigo
          </CButton>
        </div>

        <div class="faq-listagem__filtros">
          <CRow>
            <CCol md="5">
              <div class="filtro-search-wrapper">
                <fa-icon icon="search" class="filtro-search-icon" />
                <input
                  class="form-control filtro-search-input"
                  type="text"
                  v-model="filtros.termo"
                  placeholder="Buscar por título ou palavra-chave..."
                />
              </div>
            </CCol>
            <CCol md="4">
              <select class="form-control" v-model="filtros.tipo">
                <option value="">Todos os tipos</option>
                <option v-for="tipo in tipos" :key="tipo.tipo" :value="tipo.tipo">
                  {{ tipo.label }}
                </option>
              </select>
            </CCol>
            <CCol md="3">
              <select class="form-control" v-model="filtros.sistema">
                <option value="">Todos os sistemas</option>
                <option
                  v-for="sistema in sistemas"
                  :key="sistema.campo"
                  :value="sistema.campo"
                >
                  {{ descricaoSistemaAcesso(sistema.campo) }}
                </option>
              </select>
            </CCol>
          </CRow>
        </div>

        <EmptyState
          v-if="!loading && items.length === 0"
          icon="inbox"
          title="Nenhum artigo cadastrado"
          description="Cadastre o primeiro artigo para montar a base de conhecimento dos módulos."
        />

        <div v-else class="faq-listagem__table-wrapper">
          <table class="faq-listagem__table">
            <thead>
              <tr>
                <th class="col-id">#</th>
                <th>Título</th>
                <th class="col-tipo">Tipo</th>
                <th class="col-sistemas">Disponível em</th>
                <th class="col-acoes"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="itemsFiltrados.length === 0">
                <td colspan="5" class="faq-listagem__empty">
                  <p>Nenhum artigo encontrado com os filtros aplicados</p>
                </td>
              </tr>
              <tr
                v-for="(artigo, index) in itemsFiltrados"
                :key="artigo.id"
                class="faq-listagem__row"
              >
                <td class="col-id">{{ index + 1 }}</td>
                <td>
                  <span class="faq-listagem__titulo">{{ artigo.titulo }}</span>
                  <div
                    class="faq-listagem__palavras"
                    v-if="palavrasChave(artigo)"
                  >
                    {{ palavrasChave(artigo) }}
                  </div>
                </td>
                <td class="col-tipo">
                  <span
                    class="faq-listagem__tipo"
                    :style="{ borderColor: corTipo(artigo.tipo) }"
                  >
                    <fa-icon
                      v-if="iconeDoTipo(artigo.tipo)"
                      :icon="iconeDoTipo(artigo.tipo)"
                      :style="{ color: corTipo(artigo.tipo) }"
                    />
                    {{ labelTipo(artigo.tipo) }}
                  </span>
                </td>
                <td class="col-sistemas">
                  <span
                    v-for="campo in sistemasDoArtigo(artigo)"
                    :key="campo"
                    class="faq-listagem__badge"
                  >
                    {{ descricaoSistemaAcesso(campo) }}
                  </span>
                  <span
                    v-if="sistemasDoArtigo(artigo).length === 0"
                    class="faq-listagem__badge faq-listagem__badge--vazio"
                  >
                    Nenhum
                  </span>
                </td>
                <td class="actions">
                  <CButton
                    size="sm"
                    color="primary"
                    @click="openEdit(artigo)"
                    title="Alterar"
                  >
                    <fa-icon icon="pencil-alt" />
                  </CButton>
                  <CButton
                    size="sm"
                    color="danger"
                    @click="deleteConfirm(artigo.id)"
                    title="Excluir"
                  >
                    <fa-icon icon="trash-alt" />
                  </CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </CTab>
  </CTabs>
</template>

<script>
import { get, del } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import EmptyState from "@/components/EmptyState";
import FaqFormPage from "./FaqFormPage.vue";
import {
  descricaoPluralModulo,
  descricaoSingularModulo,
  descricaoSistemaAcesso,
} from "@/helpers/cargas";
import {
  defaultFaq,
  normalizeFaqDaApi,
  sistemasSelecionados,
} from "../helpers/faqDefaults";
import { SISTEMAS_ACESSO, iconeTipo } from "../helpers/faqLabels";

export default {
  name: "FaqListagem",
  components: {
    Loading,
    EmptyState,
    FaqFormPage,
  },
  data() {
    return {
      loading: false,
      items: [],
      tipos: [],
      item: defaultFaq(),
      sistemas: SISTEMAS_ACESSO,
      filtros: {
        termo: "",
        tipo: "",
        sistema: "",
      },
    };
  },
  computed: {
    /**
     * O endpoint devolve a lista completa, sem paginação, então o filtro é
     * aplicado aqui mesmo.
     */
    itemsFiltrados() {
      const termo = this.filtros.termo.trim().toLowerCase();

      return this.items.filter((artigo) => {
        if (this.filtros.tipo && artigo.tipo !== this.filtros.tipo) {
          return false;
        }

        if (this.filtros.sistema && !artigo[this.filtros.sistema]) {
          return false;
        }

        if (!termo) {
          return true;
        }

        const titulo = (artigo.titulo || "").toLowerCase();

        return (
          titulo.indexOf(termo) !== -1 ||
          this.palavrasChave(artigo).toLowerCase().indexOf(termo) !== -1
        );
      });
    },
    tiposPorChave() {
      return this.tipos.reduce((mapa, tipo) => {
        mapa[tipo.tipo] = tipo;
        return mapa;
      }, {});
    },
  },
  methods: {
    descricaoPluralModulo,
    descricaoSingularModulo,
    descricaoSistemaAcesso,
    palavrasChave(artigo) {
      const palavras = artigo.palavras_chave || [];

      return palavras
        .map((palavra) => (palavra && palavra.text ? palavra.text : palavra))
        .join(", ");
    },
    sistemasDoArtigo(artigo) {
      return sistemasSelecionados(artigo);
    },
    labelTipo(tipo) {
      const cadastrado = this.tiposPorChave[tipo];

      return cadastrado ? cadastrado.label : tipo;
    },
    corTipo(tipo) {
      const cadastrado = this.tiposPorChave[tipo];

      return cadastrado ? cadastrado.cor : "#d0d5dd";
    },
    iconeDoTipo(tipo) {
      const cadastrado = this.tiposPorChave[tipo];

      return cadastrado ? iconeTipo(cadastrado.icone) : "";
    },
    loadTipos() {
      get("/admin/faq/tipos").then((json) => {
        this.tipos = json;
      });
    },
    loadFaq() {
      this.loading = true;

      get("/admin/faq")
        .then((json) => {
          this.items = json.map(normalizeFaqDaApi);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.item = defaultFaq();
      this.$modal.show("faqEdit");
    },
    openEdit(artigo) {
      this.item = normalizeFaqDaApi(artigo);
      this.$modal.show("faqEdit");
    },
    closeEdit() {
      this.$modal.hide("faqEdit");
      this.loadFaq();
    },
    deleteConfirm(id) {
      this.$swal({
        title: "Confirma Remover ?",
        text: "Essa ação não poderá ser desfeita",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (!res.value) {
            return;
          }

          this.loading = true;

          del(`/admin/faq/${id}`)
            .then((json) => {
              this.$swal({
                title: json.message,
                icon: "success",
              });
              this.loadFaq();
            })
            .catch((err) => {
              this.$swal({
                title: err.message,
                icon: "error",
              });
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(() => {});
    },
  },
  beforeMount() {
    this.loadFaq();
    this.loadTipos();
  },
};
</script>

<style scoped>
/* ===== HEADER ===== */
.faq-listagem__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.faq-listagem__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c2c2c;
}

/* ===== FILTROS ===== */
.faq-listagem__filtros {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.faq-listagem__filtros .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
}

.faq-listagem__filtros .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.filtro-search-wrapper {
  position: relative;
}

.filtro-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  z-index: 1;
}

.filtro-search-input {
  padding-left: 36px !important;
}

/* ===== TABELA ===== */
.faq-listagem__table-wrapper {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow-x: auto;
  background: #fff;
}

.faq-listagem__table {
  width: 100%;
  border-collapse: collapse;
}

.faq-listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.faq-listagem__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px;
  white-space: nowrap;
  text-align: left;
}

.faq-listagem__table td {
  padding: 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  vertical-align: middle;
}

.faq-listagem__row:last-child td {
  border-bottom: none;
}

.faq-listagem__row:hover {
  background: #f9fafb;
}

.col-id {
  width: 50px;
  text-align: center;
}

.col-tipo {
  width: 150px;
}

.col-sistemas {
  width: 320px;
}

.col-acoes {
  width: 110px;
}

.faq-listagem__titulo {
  font-weight: 500;
  color: #1d2939;
}

.faq-listagem__palavras {
  font-size: 11px;
  color: #98a2b3;
  margin-top: 2px;
}

.faq-listagem__tipo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-left-width: 3px;
  border-radius: 4px;
  padding: 3px 8px;
}

.faq-listagem__badge {
  display: inline-block;
  font-size: 11px;
  color: #3a3e81;
  background: rgba(58, 62, 129, 0.08);
  border-radius: 10px;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  white-space: nowrap;
}

.faq-listagem__badge--vazio {
  color: #b42318;
  background: rgba(180, 35, 24, 0.08);
}

/* ===== EMPTY ===== */
.faq-listagem__empty {
  text-align: center;
  padding: 40px 20px !important;
  color: #98a2b3;
}

.faq-listagem__empty p {
  font-size: 13px;
  margin: 0;
}
</style>
